import { getAttachmentSource } from "@nijiurachan/js/pure/attachment-source";
import { ApiError } from "@/shared/api/errors";

export { getAttachmentSource };

/** Mirrors backend DEFAULT_BOARD_CONFIG.limits.maxFileSizeBytes. */
export const DEFAULT_MAX_ATTACHMENT_BYTES: number = 20 * 1024 * 1024;

/** Build-time client-side guard; the presign API remains the final authority. */
export const MAX_ATTACHMENT_BYTES: number = maxAttachmentBytesFromEnv(
  import.meta.env.VITE_MAX_ATTACHMENT_BYTES,
);

function maxAttachmentBytesFromEnv(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0
    ? parsed
    : DEFAULT_MAX_ATTACHMENT_BYTES;
}

/**
 * 添付ファイルをハッシュ計算用に読み込む。
 * サイズ超過はArrayBuffer化より前に拒否し、presign側の検査も残す。
 */
export async function readAttachmentForUpload(
  file: Pick<File, "arrayBuffer" | "size">,
): Promise<ArrayBuffer> {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new ApiError(
      `添付ファイルは${MAX_ATTACHMENT_BYTES}バイト以下にしてください`,
      undefined,
      "ATTACHMENT_TOO_LARGE",
    );
  }
  return file.arrayBuffer();
}

/** 添付ファイル本体のSHA-256を小文字hexで返す。 */
export async function sha256(input: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", input);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

const ATTACHMENT_MIME_ALIASES: Readonly<Record<string, string>> = {
  "image/jpg": "image/jpeg",
};

/**
 * ブラウザ由来のMIME文字列をallowlist照合可能な形へ正規化する。
 * backend `normalizeMime` および njs `normalizeAttachmentMimeType` と挙動を揃え、
 * presign時MIMEとPUT時Content-Typeを一致させる。
 */
export function normalizeAttachmentMimeType(type: string): string {
  const base = type.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return ATTACHMENT_MIME_ALIASES[base] ?? base;
}
